import React from 'react';
import TopNav from '../components/TopNav';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Rocket, Zap, Building2, Target } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function BusinessBuilder() {
  return (
    <div className="min-h-screen flex flex-col">
      <TopNav />
      
      <main className="flex-grow">
        <div className="bg-gradient-to-b from-muted/50 to-background py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <Badge className="mb-4" variant="secondary">Services</Badge>
              <h1 className="text-5xl font-semibold tracking-tight mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Business Builder
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                We build, automate, and scale businesses from the ground up. Whether you need a complete solution or strategic automation, we can help.
              </p>
            </div>
            
            {/* Main Message */}
            <Card className="rounded-xl mb-12 bg-gradient-to-br from-primary/5 to-background border-2" style={{ borderColor: 'var(--ap-blue)' }}>
              <CardContent className="pt-8 pb-8">
                <p className="text-xl leading-relaxed text-center mb-4">
                  We specialize in building automated businesses that scale efficiently.
                </p>
                <p className="text-muted-foreground text-center">
                  From concept to launch, we handle the technical complexity so you can focus on growth.
                </p>
              </CardContent>
            </Card>
            
            {/* Services Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
              <Card className="rounded-xl" data-testid="service-card">
                <CardHeader>
                  <Building2 className="h-10 w-10 mb-3" style={{ color: 'var(--ap-blue)' }} />
                  <CardTitle className="text-xl">Full Business Builds</CardTitle>
                </CardHeader>
                <CardContent className="text-muted-foreground">
                  <p className="text-sm">
                    Complete end-to-end business solutions built with automation at the core. We handle everything from concept to deployment.
                  </p>
                </CardContent>
              </Card>
              
              <Card className="rounded-xl" data-testid="service-card">
                <CardHeader>
                  <Zap className="h-10 w-10 mb-3" style={{ color: 'var(--ap-blue)' }} />
                  <CardTitle className="text-xl">Automation Integration</CardTitle>
                </CardHeader>
                <CardContent className="text-muted-foreground">
                  <p className="text-sm">
                    Transform your existing operations with strategic automation. We identify opportunities and implement scalable solutions.
                  </p>
                </CardContent>
              </Card>
              
              <Card className="rounded-xl" data-testid="service-card">
                <CardHeader>
                  <Rocket className="h-10 w-10 mb-3" style={{ color: 'var(--ap-blue)' }} />
                  <CardTitle className="text-xl">Rapid Prototypes</CardTitle>
                </CardHeader>
                <CardContent className="text-muted-foreground">
                  <p className="text-sm">
                    Quick validation of business concepts with functional prototypes. Test your ideas before committing to full development.
                  </p>
                </CardContent>
              </Card>
              
              <Card className="rounded-xl" data-testid="service-card">
                <CardHeader>
                  <Target className="h-10 w-10 mb-3" style={{ color: 'var(--ap-blue)' }} />
                  <CardTitle className="text-xl">Strategic Consulting</CardTitle>
                </CardHeader>
                <CardContent className="text-muted-foreground">
                  <p className="text-sm">
                    Expert guidance on automation strategy, technology decisions, and scalability planning for your business.
                  </p>
                </CardContent>
              </Card>
            </div>
            
            {/* What Makes Us Different */}
            <Card className="rounded-xl mb-8 bg-muted/30">
              <CardHeader>
                <CardTitle className="text-2xl">What Makes Us Different</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-muted-foreground">
                <div>
                  <p className="font-semibold text-foreground mb-2">Automation-First Approach</p>
                  <p className="text-sm">Every business we build is designed to scale through automation, not headcount. We optimize for efficiency from day one.</p>
                </div>
                
                <div>
                  <p className="font-semibold text-foreground mb-2">Speed to Market</p>
                  <p className="text-sm">We move fast. Our streamlined processes and proven frameworks mean you get to market quickly without sacrificing quality.</p>
                </div>
                
                <div>
                  <p className="font-semibold text-foreground mb-2">Long-Term Partnership</p>
                  <p className="text-sm">We're not just service providers. We invest in relationships and often partner with businesses we believe in.</p>
                </div>
              </CardContent>
            </Card>
            
            {/* CTA */}
            <div className="text-center">
              <Card className="rounded-xl bg-gradient-to-br from-primary/10 to-background border-2 inline-block" style={{ borderColor: 'var(--ap-blue)' }}>
                <CardContent className="pt-8 pb-8 px-12">
                  <h3 className="text-2xl font-semibold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    Ready to Build?
                  </h3>
                  <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                    Let's discuss your project and explore how we can help bring your vision to life.
                  </p>
                  <Link to="/contact">
                    <Button size="lg" className="rounded-xl">
                      Get in Touch
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
